import { AuditOutlined, DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons'
import {
  Box,
  Button,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid2 as Grid,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import Card from 'components/@extended/Card'

dayjs.extend(utc)
import Paper from 'components/@extended/Paper'
import { TextField } from 'components/@extended/TextField'
import ComponentDisplay from 'components/ComponentDisplay'
import ComponentField from 'components/ComponentField'
import { deleteWithToken, postWithToken } from 'hooks/http'
import useAuth from 'hooks/useAuth'
import { useFetchMetricDetails } from 'hooks/useComponents'
import { useSnackbar } from 'notistack'
import type { FC, JSX } from 'react'
import { startTransition, useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  ComponentSourceType,
  ComponentType,
  ComponentType as ComponentTypeEnum,
  CompositeActualValueItem,
  getCompositeFunction,
  mergeCompositeTargetIntoMetric,
  normalizeCompositeTypeValue,
} from 'types/components'
import { ThemeMode } from 'types/config'
import { formatDisplayDate, formatDisplayDateTime, DATE_FORMAT } from 'types/periods'

interface ColumnProps {
  id: string
  label: string
  minWidth: number
  align?: 'right' | 'left' | 'inherit' | 'center' | 'justify' | undefined
  format?: (value: any, row: any) => string | JSX.Element
}

const EditMetric: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const componentId = searchParams.get('componentId')
  const organizationId = searchParams.get('organizationId')
  const periodId = searchParams.get('periodId')
  const userId = searchParams.get('userId')

  const { token } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const [isSaving, setIsSaving] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [revisionToDelete, setRevisionToDelete] = useState<number | null>(null)

  const shouldFetch = !!token && !!componentId && !!organizationId && !!periodId
  console.log('Should fetch:', shouldFetch, { token, componentId, organizationId, periodId })

  const { metricDetails, isLoading, isError, revalidate } = useFetchMetricDetails(
    token!,
    {
      componentId: componentId!,
      organizationId: organizationId!,
      periodId: periodId!,
      ...(userId !== 'null' && { userId }),
    },
    shouldFetch
  )

  const [metricValue, setMetricValue] = useState('')
  const [compositeValues, setCompositeValues] = useState<{ values: CompositeActualValueItem[] }>({ values: [] })
  const [effectiveDate, setEffectiveDate] = useState<dayjs.Dayjs | null>(null)

  const theme = useTheme()

  useEffect(() => {
    if (metricDetails) {
      setMetricValue(metricDetails.revisions[0].value)

      // Initialize effective date from the latest revision
      const latestRevision = metricDetails.revisions[0]
      if (latestRevision && 'effectiveDate' in latestRevision && latestRevision.effectiveDate) {
        // Parse the date as UTC directly to avoid timezone shift
        const dateStr = latestRevision.effectiveDate as string
        if (/^\d{2}-\d{2}-\d{4}/.test(dateStr)) {
          // If it's in MM-DD-YYYY format, parse it explicitly as UTC
          setEffectiveDate(dayjs.utc(dateStr, 'MM-DD-YYYY HH:mm:ss'))
        } else {
          // Otherwise use default parsing
          setEffectiveDate(dayjs.utc(dateStr))
        }
      } else {
        // Default to today if no effective date exists
        setEffectiveDate(dayjs().utc())
      }

      // Initialize composite values if this is a composite component
      if (metricDetails.component.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE) {
        const mergedCompositeValues = mergeCompositeTargetIntoMetric(
          metricDetails.revisions[0].value,
          metricDetails.latestTarget?.value,
          metricDetails.component.componentType as ComponentType
        )

        // If mergeCompositeTargetIntoMetric didn't return any values (no target structure),
        // parse the metric value directly
        if (!mergedCompositeValues.values || mergedCompositeValues.values.length === 0) {
          try {
            const directParsed = JSON.parse(metricDetails.revisions[0].value) as { values: CompositeActualValueItem[] }
            if (directParsed.values && Array.isArray(directParsed.values)) {
              setCompositeValues(directParsed)
            } else {
              setCompositeValues({ values: [] })
            }
          } catch (e) {
            console.error('Failed to parse composite value:', e)
            setCompositeValues({ values: [] })
          }
        } else {
          setCompositeValues(mergedCompositeValues)
        }
      }
    }
  }, [metricDetails])

  const handleSave = async () => {
    if (!effectiveDate) {
      enqueueSnackbar('Please select an effective date', { variant: 'error' })
      return
    }

    setIsSaving(true)
    try {
      await postWithToken('components/metrics/update', token!, {
        componentId,
        periodId,
        organizationId,
        value: typeof metricValue === 'string' ? metricValue : String(metricValue),
        effectiveDate: effectiveDate.utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
        ...(userId !== 'null' && { userId }),
      })

      enqueueSnackbar('Actual updated successfully', { variant: 'success' })
      revalidate()
    } catch (error: any) {
      if (error.response) {
        switch (error.response.status) {
          case 400:
            enqueueSnackbar('Invalid input data. Please check your entries.', { variant: 'error' })
            break
          case 403:
            enqueueSnackbar('You do not have permission to update this actual.', { variant: 'error' })
            break
          default:
            enqueueSnackbar('Failed to update actual. Please try again.', { variant: 'error' })
        }
      } else {
        enqueueSnackbar('An unexpected error occurred. Please try again.', { variant: 'error' })
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleAddCompositeValue = () => {
    const newCompositeValues = {
      values: [
        ...compositeValues.values,
        {
          label: `Value ${compositeValues.values.length + 1}`,
          description: 'Enter description',
          value: '0',
          target: '',
        },
      ],
    }
    setCompositeValues(newCompositeValues)
    setMetricValue(JSON.stringify(newCompositeValues))
  }

  const handleRemoveCompositeValue = (valueIndex: number) => {
    const newValues = {
      values: compositeValues.values.filter((_, i) => i !== valueIndex),
    }
    setCompositeValues(newValues)
    setMetricValue(JSON.stringify(newValues))
  }

  const handleUpdateCompositeValue = (
    valueIndex: number,
    field: 'label' | 'description' | 'value' | 'target',
    newValue: string
  ) => {
    const newValues = {
      values: compositeValues.values.map((item, i) => (i === valueIndex ? { ...item, [field]: newValue } : item)),
    }
    setCompositeValues(newValues)
    setMetricValue(JSON.stringify(newValues))
  }

  const handleUpdateComponentType = (valueIndex: number, newType: ComponentTypeEnum) => {
    const newValues = {
      values: compositeValues.values.map((item, i) => (i === valueIndex ? { ...item, componentType: newType } : item)),
    }
    setCompositeValues(newValues)
    setMetricValue(JSON.stringify(newValues))
  }

  const handleDeleteClick = (revision: number) => {
    setRevisionToDelete(revision)
    setOpenDeleteDialog(true)
  }

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false)
    setRevisionToDelete(null)
  }

  const handleConfirmDelete = async () => {
    if (revisionToDelete === null) return

    try {
      const params = new URLSearchParams({
        componentId: componentId!,
        organizationId: organizationId!,
        periodId: periodId!,
        rootOrganizationId: metricDetails.organization.rootOrganizationId,
        revision: revisionToDelete.toString(),
      })

      if (userId && userId !== 'null') {
        params.append('userId', userId)
      }

      await deleteWithToken(`components/metrics?${params.toString()}`, token!)

      enqueueSnackbar('Actual revision deleted successfully', { variant: 'success' })

      // Revalidate to check if there are more revisions
      await revalidate()

      // Check if there are no more revisions left after deletion
      if (metricDetails && metricDetails.revisions.length === 1) {
        // This was the last revision, navigate back to manage metrics
        startTransition(() => {
          navigate('/admin/components/actuals')
        })
      }
    } catch {
      enqueueSnackbar('Failed to delete actual revision. Please try again.', { variant: 'error' })
    } finally {
      handleCloseDeleteDialog()
    }
  }

  const hasCompositeTargetStructure = (): boolean => {
    // Check if the current composite values have target fields populated
    // This indicates they were derived from a composite target structure
    if (compositeValues.values && compositeValues.values.length > 0) {
      return compositeValues.values.some((item) => item.target && item.target !== '')
    }
    return false
  }

  if (isLoading) {
    return <CircularProgress />
  }

  if (isError || !metricDetails) {
    return <Typography variant="h6">Error loading actual details</Typography>
  }

  const orgSettings = metricDetails.coreSettings
  const isComposite = metricDetails.component.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE
  const isPercentage = metricDetails.component.componentType === ComponentType.COMPONENT_TYPE_PERCENTAGE
  const isPeriodClosed = metricDetails.period.isClosed || false
  const compositeType = (() => {
    if (!metricDetails.component.sourceInfo) return normalizeCompositeTypeValue()
    let sourceInfo = metricDetails.component.sourceInfo
    if (typeof sourceInfo === 'string') {
      try {
        sourceInfo = JSON.parse(sourceInfo)
      } catch {
        return normalizeCompositeTypeValue()
      }
    }
    return normalizeCompositeTypeValue((sourceInfo as any)?.compositeType)
  })()

  const planColumns: ColumnProps[] = [
    { id: 'name', label: 'Name', minWidth: 170, format: (value, row) => `${row.firstName} ${row.lastName}` },
    {
      id: 'approvalStatus',
      label: 'Status',
      minWidth: 100,
      format: (value) => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 80,
      align: 'center',
      format: (value, row) => (
        <Box display="flex" justifyContent="center">
          <Tooltip title="View Plan">
            <IconButton
              color="primary"
              onClick={() => {
                startTransition(() => {
                  navigate(`/employee/${row.userId}/compplan`)
                })
              }}
            >
              <EyeOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Plan">
            <IconButton
              color="primary"
              onClick={() => {
                startTransition(() => {
                  navigate(`/create-comp-plan?userId=${row.userId}`, {
                    state: { from: location.pathname },
                  })
                })
              }}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Approval Batch">
            <IconButton
              color="primary"
              onClick={() => {
                startTransition(() => {
                  navigate(`/admin/approvals/${row.approvalBatchId}/edit`)
                })
              }}
            >
              <AuditOutlined />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  const revisionColumns: ColumnProps[] = [
    { id: 'revision', label: 'Revision', minWidth: 100 },
    {
      id: 'approvalStatus',
      label: 'Status',
      minWidth: 150,
      format: (value) => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
    },
    {
      id: 'value',
      label: 'Value',
      minWidth: 120,
      format: (value) => {
        console.log('component', metricDetails.component.sourceInfo)
        // For composite metrics, ComponentDisplay can handle the JSON string directly
        let displayValue = value
        if (metricDetails.component.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE) {
          // ComponentDisplay expects the original JSON string for composite values
          displayValue = value
        }

        return (
          <ComponentDisplay
            componentType={metricDetails.component.componentType as ComponentType}
            value={displayValue}
            currency={orgSettings.currency}
            currencyDecimals={orgSettings.currencyDecimals}
            sourceType={metricDetails.component.sourceType as ComponentSourceType}
            compositeFunction={getCompositeFunction(metricDetails.component.sourceInfo)}
            fontSize="inherit"
          />
        )
      },
    },
    { id: 'createdAt', label: 'Created At', minWidth: 170, format: (value) => formatDisplayDateTime(value) },
    {
      id: 'effectiveDate',
      label: 'Effective Date',
      minWidth: 140,
      format: (value) => (value ? formatDisplayDate(value) : '-'),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 80,
      align: 'center',
      format: (value, row) => (
        <Box display="flex" justifyContent="center">
          {row.approvalBatchId && (
            <Tooltip title="View Approval Batch">
              <IconButton
                color="primary"
                onClick={() => {
                  startTransition(() => {
                    navigate(`/admin/approvals/${row.approvalBatchId}/edit`)
                  })
                }}
              >
                <AuditOutlined />
              </IconButton>
            </Tooltip>
          )}
          {metricDetails.plans.length === 0 && (
            <Tooltip title="Delete Revision">
              <IconButton color="error" onClick={() => handleDeleteClick(row.revision)}>
                <DeleteOutlined />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ]

  return (
    <>
      <Typography variant="h2" gutterBottom>
        Edit Actual
      </Typography>
      <Grid container spacing={3}>
        {isComposite ? (
          <>
            <Grid size={{ xs: 12 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h4" gutterBottom>
                        Component Information
                      </Typography>
                      <Grid container spacing={2} sx={{ mt: 2, pl: 2, pr: 2 }}>
                        <Grid size={{ xs: 4 }}>
                          <Typography>Component:</Typography>
                        </Grid>
                        <Grid size={{ xs: 8 }}>
                          <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                            {metricDetails.component.label}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                          <Typography>Team:</Typography>
                        </Grid>
                        <Grid size={{ xs: 8 }}>
                          <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                            {metricDetails.organization.name}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                          <Typography>Period:</Typography>
                        </Grid>
                        <Grid size={{ xs: 8 }}>
                          <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                            {metricDetails.period.label}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 8 }}>
                          {!metricDetails.component.isOrganizationLevel && (
                            <>
                              <Grid size={{ xs: 4 }}>
                                <Typography>{metricDetails.user ? 'Employee:' : ''}</Typography>
                              </Grid>
                              <Grid size={{ xs: 8 }}>
                                {userId === '00000000-0000-0000-0000-000000000000' ? (
                                  <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                                    Values represent defaults used when creating employee plans
                                  </Typography>
                                ) : metricDetails.user ? (
                                  <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                                    {`${metricDetails.user.firstName} ${metricDetails.user.lastName}`}
                                  </Typography>
                                ) : null}
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h4" gutterBottom>
                        Composite Information
                      </Typography>
                      <Typography variant="body2" color="textSecondary" component="ul" sx={{ m: 0, pl: 2 }}>
                        <li>The composite target is optional</li>
                        <li>
                          When targets are present:
                          <ul>
                            <li>An achievement ratio is calculated for each composite item</li>
                            <li>The composite function is applied to these ratios</li>
                            <li>The result is evaluated against the overall target</li>
                          </ul>
                        </li>
                        <li>
                          When no targets are present:
                          <ul>
                            <li>The composite function is applied directly to the values</li>
                          </ul>
                        </li>
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Card variant="outlined" sx={{ mb: 3, height: '100%', position: 'relative' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h4" gutterBottom>
                      Actual Value
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body1" fontWeight="bold" sx={{ mr: 2 }}>
                        Applied Value:
                      </Typography>
                      <ComponentDisplay
                        componentType={metricDetails.component.componentType as ComponentType}
                        value={
                          isComposite
                            ? JSON.stringify(compositeValues) // Use the merged composite values for display
                            : metricValue
                        }
                        currency={orgSettings.currency}
                        currencyDecimals={orgSettings.currencyDecimals}
                        sourceType={metricDetails.component.sourceType as ComponentSourceType}
                        compositeFunction={getCompositeFunction(metricDetails.component.sourceInfo)}
                        compositeType={compositeType}
                        lowerIsBetter={metricDetails.component.lowerIsBetter || false}
                        fontSize="1.25rem"
                        fontWeight="bold"
                        color="primary"
                      />
                    </Box>
                  </Box>

                  {isComposite ? (
                    <>
                      <Box mb={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="h5">Composite Values</Typography>
                          {!hasCompositeTargetStructure() && (
                            <Button startIcon={<PlusOutlined />} size="small" onClick={handleAddCompositeValue}>
                              Add Value
                            </Button>
                          )}
                        </Box>
                        <TableContainer>
                          <Table stickyHeader size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
                            <TableHead
                              sx={{
                                '& th': {
                                  borderTop: `1px solid ${theme.palette.divider}`,
                                  borderBottom: `2px solid ${theme.palette.divider} !important`,
                                },
                              }}
                            >
                              <TableRow>
                                <TableCell width={!hasCompositeTargetStructure() ? '15%' : '17%'}>
                                  <strong>Label</strong>
                                </TableCell>
                                <TableCell width={!hasCompositeTargetStructure() ? '40%' : '45%'}>
                                  <strong>Description</strong>
                                </TableCell>
                                <TableCell width="10%">
                                  <strong>Type</strong>
                                </TableCell>
                                <TableCell width="15%">
                                  <strong>Target</strong>
                                </TableCell>
                                <TableCell width="15%">
                                  <strong>Value</strong>
                                </TableCell>
                                {!hasCompositeTargetStructure() && (
                                  <TableCell width="5%" align="center">
                                    <strong>Actions</strong>
                                  </TableCell>
                                )}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {compositeValues.values.map((item, valueIndex) => {
                                const hasTargetStructure = hasCompositeTargetStructure()

                                return (
                                  <TableRow key={valueIndex}>
                                    <TableCell>
                                      {hasTargetStructure ? (
                                        <Typography variant="body1" sx={{ py: 1 }}>
                                          {item.label}
                                        </Typography>
                                      ) : (
                                        <TextField
                                          value={item.label}
                                          onChange={(e) =>
                                            handleUpdateCompositeValue(valueIndex, 'label', e.target.value)
                                          }
                                          fullWidth
                                          variant="outlined"
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {hasTargetStructure ? (
                                        <Typography variant="body1" sx={{ py: 1 }}>
                                          {item.description}
                                        </Typography>
                                      ) : (
                                        <TextField
                                          value={item.description}
                                          onChange={(e) =>
                                            handleUpdateCompositeValue(valueIndex, 'description', e.target.value)
                                          }
                                          fullWidth
                                          variant="outlined"
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {hasTargetStructure || !isPercentage ? (
                                        <Typography variant="body1" sx={{ py: 1 }}>
                                          {(() => {
                                            const type = item.componentType || metricDetails.component.componentType
                                            switch (type) {
                                              case ComponentTypeEnum.COMPONENT_TYPE_INTEGER:
                                                return 'Integer'
                                              case ComponentTypeEnum.COMPONENT_TYPE_CURRENCY:
                                                return 'Currency'
                                              case ComponentTypeEnum.COMPONENT_TYPE_PERCENTAGE:
                                                return 'Percentage'
                                              case ComponentTypeEnum.COMPONENT_TYPE_BOOL:
                                                return 'Boolean'
                                              case ComponentTypeEnum.COMPONENT_TYPE_FLOAT:
                                                return 'Decimal'
                                              default:
                                                return type
                                            }
                                          })()}
                                        </Typography>
                                      ) : (
                                        <Select
                                          value={item.componentType || metricDetails.component.componentType}
                                          onChange={(e) =>
                                            handleUpdateComponentType(valueIndex, e.target.value as ComponentTypeEnum)
                                          }
                                          fullWidth
                                          variant="outlined"
                                          sx={{
                                            '& .MuiMenuItem-root:hover': {
                                              backgroundColor:
                                                theme.palette.mode === ThemeMode.DARK
                                                  ? theme.palette.primary.darker
                                                  : theme.palette.primary.lighter,
                                            },
                                            '& .MuiMenuItem-root.Mui-selected:hover': {
                                              backgroundColor: theme.palette.action.selected,
                                            },
                                            fontSize: 'inherit',
                                            borderRadius: '21px',
                                          }}
                                        >
                                          <MenuItem value={ComponentTypeEnum.COMPONENT_TYPE_INTEGER}>Integer</MenuItem>
                                          <MenuItem value={ComponentTypeEnum.COMPONENT_TYPE_CURRENCY}>
                                            Currency
                                          </MenuItem>
                                          <MenuItem value={ComponentTypeEnum.COMPONENT_TYPE_PERCENTAGE}>
                                            Percentage
                                          </MenuItem>
                                          <MenuItem value={ComponentTypeEnum.COMPONENT_TYPE_BOOL}>Boolean</MenuItem>
                                          <MenuItem value={ComponentTypeEnum.COMPONENT_TYPE_FLOAT}>Decimal</MenuItem>
                                        </Select>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {item.target ? (
                                        <ComponentDisplay
                                          componentType={
                                            item.componentType ||
                                            (metricDetails.component.componentType as ComponentType)
                                          }
                                          value={item.target}
                                          currency={orgSettings.currency}
                                          currencyDecimals={orgSettings.currencyDecimals}
                                          decimalPlaces={2}
                                          fontSize="inherit"
                                        />
                                      ) : (
                                        <Typography variant="body1" color="textSecondary" sx={{ py: 1 }}>
                                          -
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <ComponentField
                                        componentType={
                                          item.componentType || (metricDetails.component.componentType as ComponentType)
                                        }
                                        label=""
                                        value={item.value}
                                        onChange={(value) => handleUpdateCompositeValue(valueIndex, 'value', value)}
                                        name={`composite-${valueIndex}`}
                                        currency={orgSettings.currency}
                                        currencyDecimals={orgSettings.currencyDecimals}
                                        decimalPlaces={metricDetails.component.numericDecimalPlaces ?? 2}
                                      />
                                    </TableCell>
                                    {!hasCompositeTargetStructure() && (
                                      <TableCell align="center">
                                        <IconButton size="small" onClick={() => handleRemoveCompositeValue(valueIndex)}>
                                          <DeleteOutlined />
                                        </IconButton>
                                      </TableCell>
                                    )}
                                  </TableRow>
                                )
                              })}
                              {compositeValues.values.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={!hasCompositeTargetStructure() ? 6 : 5} align="center">
                                    <Typography variant="body2" color="textSecondary">
                                      No values added yet. Click "Add Value" to begin.
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </>
                  ) : (
                    <Box mb={2}>
                      <ComponentField
                        componentType={metricDetails.component.componentType as ComponentType}
                        label="Value"
                        value={metricValue}
                        onChange={(value) => setMetricValue(value)}
                        name="metric-value"
                        currency={orgSettings.currency}
                        currencyDecimals={orgSettings.currencyDecimals}
                        decimalPlaces={metricDetails.component.numericDecimalPlaces ?? 2}
                        shrink
                        pill
                      />
                    </Box>
                  )}

                  <Box mb={2} sx={{ maxWidth: { xs: '100%', md: '50%' } }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="Effective Date *"
                        value={effectiveDate}
                        onChange={(newValue) => {
                          if (newValue) {
                            const dateString = newValue.format('YYYY-MM-DD')
                            const utcDate = dayjs.utc(dateString)
                            setEffectiveDate(utcDate)
                          } else {
                            setEffectiveDate(null)
                          }
                        }}
                        format={DATE_FORMAT}
                        maxDate={dayjs().utc()}
                        minDate={
                          metricDetails.period.startDate ? dayjs(metricDetails.period.startDate).utc() : undefined
                        }
                        shouldDisableDate={(date) => {
                          // Only allow dates in the past within the period range
                          if (date.isAfter(dayjs(), 'day')) return true
                          if (
                            metricDetails.period.startDate &&
                            date.isBefore(dayjs(metricDetails.period.startDate), 'day')
                          )
                            return true
                          if (metricDetails.period.endDate && date.isAfter(dayjs(metricDetails.period.endDate), 'day'))
                            return true
                          return false
                        }}
                        slotProps={{
                          textField: {
                            variant: 'outlined',
                            error: !effectiveDate,
                            helperText: !effectiveDate ? 'Effective date is required' : '',
                            InputLabelProps: {
                              shrink: true,
                              sx: {
                                '&.Mui-focused': {
                                  color: (theme) => theme.palette.secondary.main,
                                },
                              },
                            },
                            sx: {
                              width: '100%',
                              '& .MuiOutlinedInput-root': {
                                borderRadius: (theme) => theme.shape.borderRadius * 5.25,
                                '& fieldset': {
                                  borderColor: (theme) => theme.palette.primary.main,
                                },
                                '&:hover fieldset': {
                                  borderColor: (theme) => theme.palette.primary.dark,
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: (theme) => theme.palette.secondary.main,
                                },
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>

                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      right: 16,
                    }}
                  >
                    <Tooltip title={isPeriodClosed ? 'Cannot save changes to a closed period' : ''}>
                      <span>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleSave}
                          disabled={isSaving || isPeriodClosed}
                        >
                          {isSaving ? <CircularProgress size={24} /> : 'Save Changes'}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </>
        ) : (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h4" gutterBottom>
                    Component Information
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 2, pl: 2, pr: 2 }}>
                    <Grid size={{ xs: 4 }}>
                      <Typography>Component:</Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }}>
                      <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                        {metricDetails.component.label}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography>Team:</Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }}>
                      <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                        {metricDetails.organization.name}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography>Period:</Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }}>
                      <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                        {metricDetails.period.label}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }}>
                      {!metricDetails.component.isOrganizationLevel && (
                        <>
                          <Grid size={{ xs: 4 }}>
                            <Typography>{metricDetails.user ? 'Employee:' : ''}</Typography>
                          </Grid>
                          <Grid size={{ xs: 8 }}>
                            {userId === '00000000-0000-0000-0000-000000000000' ? (
                              <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                                Values represent defaults used when creating employee plans
                              </Typography>
                            ) : metricDetails.user ? (
                              <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                                {`${metricDetails.user.firstName} ${metricDetails.user.lastName}`}
                              </Typography>
                            ) : null}
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" sx={{ mb: 3, height: '100%', position: 'relative' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h4" gutterBottom>
                      Actual Value
                    </Typography>
                  </Box>

                  {isComposite ? (
                    <>
                      <Box mb={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="h5">Composite Values</Typography>
                          {!hasCompositeTargetStructure() && (
                            <Button startIcon={<PlusOutlined />} size="small" onClick={handleAddCompositeValue}>
                              Add Value
                            </Button>
                          )}
                        </Box>
                        <TableContainer>
                          <Table stickyHeader size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
                            <TableHead
                              sx={{
                                '& th': {
                                  borderTop: `1px solid ${theme.palette.divider}`,
                                  borderBottom: `2px solid ${theme.palette.divider} !important`,
                                },
                              }}
                            >
                              <TableRow>
                                <TableCell width={!hasCompositeTargetStructure() ? '15%' : '17%'}>
                                  <strong>Label</strong>
                                </TableCell>
                                <TableCell width={!hasCompositeTargetStructure() ? '40%' : '45%'}>
                                  <strong>Description</strong>
                                </TableCell>
                                <TableCell width="10%">
                                  <strong>Type</strong>
                                </TableCell>
                                <TableCell width="15%">
                                  <strong>Target</strong>
                                </TableCell>
                                <TableCell width="15%">
                                  <strong>Value</strong>
                                </TableCell>
                                {!hasCompositeTargetStructure() && (
                                  <TableCell width="5%" align="center">
                                    <strong>Actions</strong>
                                  </TableCell>
                                )}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {compositeValues.values.map((item, valueIndex) => {
                                const hasTargetStructure = hasCompositeTargetStructure()

                                return (
                                  <TableRow key={valueIndex}>
                                    <TableCell>
                                      {hasTargetStructure ? (
                                        <Typography variant="body1" sx={{ py: 1 }}>
                                          {item.label}
                                        </Typography>
                                      ) : (
                                        <TextField
                                          value={item.label}
                                          onChange={(e) =>
                                            handleUpdateCompositeValue(valueIndex, 'label', e.target.value)
                                          }
                                          fullWidth
                                          variant="outlined"
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {hasTargetStructure ? (
                                        <Typography variant="body1" sx={{ py: 1 }}>
                                          {item.description}
                                        </Typography>
                                      ) : (
                                        <TextField
                                          value={item.description}
                                          onChange={(e) =>
                                            handleUpdateCompositeValue(valueIndex, 'description', e.target.value)
                                          }
                                          fullWidth
                                          variant="outlined"
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {hasTargetStructure ||
                                      item.componentType !== ComponentTypeEnum.COMPONENT_TYPE_PERCENTAGE ? (
                                        <Typography variant="body1" sx={{ py: 1 }}>
                                          {(() => {
                                            const type = item.componentType || metricDetails.component.componentType
                                            switch (type) {
                                              case ComponentTypeEnum.COMPONENT_TYPE_INTEGER:
                                                return 'Integer'
                                              case ComponentTypeEnum.COMPONENT_TYPE_CURRENCY:
                                                return 'Currency'
                                              case ComponentTypeEnum.COMPONENT_TYPE_PERCENTAGE:
                                                return 'Percentage'
                                              case ComponentTypeEnum.COMPONENT_TYPE_BOOL:
                                                return 'Boolean'
                                              case ComponentTypeEnum.COMPONENT_TYPE_FLOAT:
                                                return 'Decimal'
                                              default:
                                                return type
                                            }
                                          })()}
                                        </Typography>
                                      ) : (
                                        <Select
                                          value={item.componentType || metricDetails.component.componentType}
                                          onChange={(e) =>
                                            handleUpdateComponentType(valueIndex, e.target.value as ComponentTypeEnum)
                                          }
                                          fullWidth
                                          variant="outlined"
                                          sx={{
                                            '& .MuiMenuItem-root:hover': {
                                              backgroundColor:
                                                theme.palette.mode === ThemeMode.DARK
                                                  ? theme.palette.primary.darker
                                                  : theme.palette.primary.lighter,
                                            },
                                            '& .MuiMenuItem-root.Mui-selected:hover': {
                                              backgroundColor: theme.palette.action.selected,
                                            },
                                            fontSize: 'inherit',
                                            borderRadius: '21px',
                                          }}
                                        >
                                          <MenuItem value={ComponentTypeEnum.COMPONENT_TYPE_INTEGER}>Integer</MenuItem>
                                          <MenuItem value={ComponentTypeEnum.COMPONENT_TYPE_CURRENCY}>
                                            Currency
                                          </MenuItem>
                                          <MenuItem value={ComponentTypeEnum.COMPONENT_TYPE_PERCENTAGE}>
                                            Percentage
                                          </MenuItem>
                                          <MenuItem value={ComponentTypeEnum.COMPONENT_TYPE_BOOL}>Boolean</MenuItem>
                                          <MenuItem value={ComponentTypeEnum.COMPONENT_TYPE_FLOAT}>Decimal</MenuItem>
                                        </Select>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {item.target ? (
                                        <ComponentDisplay
                                          componentType={
                                            item.componentType ||
                                            (metricDetails.component.componentType as ComponentType)
                                          }
                                          value={item.target}
                                          currency={orgSettings.currency}
                                          currencyDecimals={orgSettings.currencyDecimals}
                                          decimalPlaces={2}
                                          fontSize="inherit"
                                        />
                                      ) : (
                                        <Typography variant="body1" color="textSecondary" sx={{ py: 1 }}>
                                          -
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <ComponentField
                                        componentType={
                                          item.componentType || (metricDetails.component.componentType as ComponentType)
                                        }
                                        label=""
                                        value={item.value}
                                        onChange={(value) => handleUpdateCompositeValue(valueIndex, 'value', value)}
                                        name={`composite-${valueIndex}`}
                                        currency={orgSettings.currency}
                                        currencyDecimals={orgSettings.currencyDecimals}
                                        decimalPlaces={metricDetails.component.numericDecimalPlaces ?? 2}
                                      />
                                    </TableCell>
                                    {!hasCompositeTargetStructure() && (
                                      <TableCell align="center">
                                        <IconButton size="small" onClick={() => handleRemoveCompositeValue(valueIndex)}>
                                          <DeleteOutlined />
                                        </IconButton>
                                      </TableCell>
                                    )}
                                  </TableRow>
                                )
                              })}
                              {compositeValues.values.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={!hasCompositeTargetStructure() ? 6 : 5} align="center">
                                    <Typography variant="body2" color="textSecondary">
                                      No values added yet. Click "Add Value" to begin.
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </>
                  ) : (
                    <Box mb={2}>
                      <ComponentField
                        componentType={metricDetails.component.componentType as ComponentType}
                        label="Value"
                        value={metricValue}
                        onChange={(value) => setMetricValue(value)}
                        name="metric-value"
                        currency={orgSettings.currency}
                        currencyDecimals={orgSettings.currencyDecimals}
                        decimalPlaces={metricDetails.component.numericDecimalPlaces ?? 2}
                        shrink
                        pill
                      />
                    </Box>
                  )}

                  <Box mb={2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="Effective Date *"
                        value={effectiveDate}
                        onChange={(newValue) => {
                          if (newValue) {
                            const dateString = newValue.format('YYYY-MM-DD')
                            const utcDate = dayjs.utc(dateString)
                            setEffectiveDate(utcDate)
                          } else {
                            setEffectiveDate(null)
                          }
                        }}
                        format={DATE_FORMAT}
                        maxDate={dayjs().utc()}
                        minDate={
                          metricDetails.period.startDate ? dayjs(metricDetails.period.startDate).utc() : undefined
                        }
                        shouldDisableDate={(date) => {
                          // Only allow dates in the past within the period range
                          if (date.isAfter(dayjs(), 'day')) return true
                          if (
                            metricDetails.period.startDate &&
                            date.isBefore(dayjs(metricDetails.period.startDate), 'day')
                          )
                            return true
                          if (metricDetails.period.endDate && date.isAfter(dayjs(metricDetails.period.endDate), 'day'))
                            return true
                          return false
                        }}
                        slotProps={{
                          textField: {
                            variant: 'outlined',
                            error: !effectiveDate,
                            helperText: !effectiveDate ? 'Effective date is required' : '',
                            InputLabelProps: {
                              shrink: true,
                              sx: {
                                '&.Mui-focused': {
                                  color: (theme) => theme.palette.secondary.main,
                                },
                              },
                            },
                            sx: {
                              width: '100%',
                              '& .MuiOutlinedInput-root': {
                                borderRadius: (theme) => theme.shape.borderRadius * 5.25,
                                '& fieldset': {
                                  borderColor: (theme) => theme.palette.primary.main,
                                },
                                '&:hover fieldset': {
                                  borderColor: (theme) => theme.palette.primary.dark,
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: (theme) => theme.palette.secondary.main,
                                },
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>

                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      right: 16,
                    }}
                  >
                    <Tooltip title={isPeriodClosed ? 'Cannot save changes to a closed period' : ''}>
                      <span>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleSave}
                          disabled={isSaving || isPeriodClosed}
                        >
                          {isSaving ? <CircularProgress size={24} /> : 'Save Changes'}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      <Typography variant="h4" gutterBottom sx={{ mt: 4 }}>
        Actual Revisions
      </Typography>
      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Table stickyHeader size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead
              sx={{
                '& th': {
                  borderTop: `1px solid ${theme.palette.divider}`,
                  borderBottom: `2px solid ${theme.palette.divider} !important`,
                },
              }}
            >
              <TableRow>
                {revisionColumns.map((column) => (
                  <TableCell key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
                    <strong>{column.label}</strong>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {metricDetails.revisions
                .sort((a, b) => b.revision - a.revision)
                .map((revision) => (
                  <TableRow key={revision.revision}>
                    {revisionColumns.map((column) => {
                      const value = revision[column.id as keyof typeof revision]
                      return (
                        <TableCell key={column.id} align={column.align}>
                          {column.format ? column.format(value, revision) : value}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="h4" gutterBottom sx={{ mt: 4 }}>
        Employee Plans using this Actual
      </Typography>
      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Table stickyHeader size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead
              sx={{
                '& th': {
                  borderTop: `1px solid ${theme.palette.divider}`,
                  borderBottom: `2px solid ${theme.palette.divider} !important`,
                },
              }}
            >
              <TableRow>
                {planColumns.map((column) => (
                  <TableCell key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
                    <strong>{column.label}</strong>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {metricDetails.plans.map((plan) => (
                <TableRow key={plan.id}>
                  {planColumns.map((column) => {
                    const value = plan[column.id as keyof typeof plan]
                    return (
                      <TableCell key={column.id} align={column.align}>
                        {column.format ? column.format(value, plan) : value}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="h4" gutterBottom sx={{ mt: 4 }}>
        Templates using this Component
      </Typography>
      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Table stickyHeader size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead
              sx={{
                '& th': {
                  borderTop: `1px solid ${theme.palette.divider}`,
                  borderBottom: `2px solid ${theme.palette.divider} !important`,
                },
              }}
            >
              <TableRow>
                <TableCell>
                  <strong>Label</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {metricDetails.templates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>{template.label}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit Template">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          startTransition(() => {
                            navigate(`/admin/templates/${template.id}/edit`)
                          })
                        }}
                      >
                        <EditOutlined />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Actual Revision Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete revision {revisionToDelete}?
            {metricDetails.revisions.length === 1 && (
              <Typography color="warning.main" fontWeight="bold" sx={{ mt: 2 }}>
                Warning: This is the last revision. Deleting it will remove the entire actual and redirect you to the
                manage actuals page.
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default EditMetric
